/**
 * Request timeout machinery for the shared API client.
 *
 * The client set no timeout for most of its life, so a backend that stopped
 * responding left requests pending forever — pages rendered an indefinite
 * spinner with no error and no way out. Every request now carries a bound.
 */

/**
 * How long a request may go unanswered before it is aborted.
 *
 * Deliberately generous: a bound that trips during normal use would turn
 * slow-but-working endpoints into failures, which is worse than the problem
 * it solves. This exists to catch the pathological case — a backend that
 * never answers — not to enforce a performance budget. Endpoints that are
 * legitimately slower should pass their own `timeoutMs`.
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 120_000;

/** Marker distinguishing our timeout abort from a caller's cancellation. */
export class RequestTimeoutError extends Error {
  /**
   * @param url - Endpoint that failed to answer, included in the message.
   * @param afterMs - The bound that was actually applied, which is not always
   *   the default — reporting the default for an overridden call would send
   *   whoever reads the log looking at the wrong number.
   */
  constructor(url: string, afterMs: number) {
    super(`Request to ${url} timed out after ${afterMs}ms`);
    this.name = 'RequestTimeoutError';
  }
}

/** A timeout armed for one request. */
export interface ArmedTimeout {
  /** Signal to hand to fetch — fires on timeout or caller cancellation. */
  signal: AbortSignal;
  /** Disarm once the request settles. */
  settle: () => void;
  /** Whether the abort came from us rather than the caller. */
  timedOut: () => boolean;
}

/**
 * Bind a timeout and any caller signal onto one controller.
 *
 * Call `settle` once the request resolves — without it the timer stays armed
 * against a completed request and accumulates under polling.
 *
 * @param url - Endpoint being requested, for the error message.
 * @param timeoutMs - Bound in milliseconds, or `null` to leave it unbounded.
 * @param external - The caller's own abort signal, if it supplied one.
 * @returns The combined signal plus its lifecycle handles.
 */
export const armTimeout = (
  url: string,
  timeoutMs: number | null,
  external: AbortSignal | undefined
): ArmedTimeout => {
  const controller = new AbortController();
  let timedOut = false;

  const timer =
    timeoutMs === null
      ? null
      : setTimeout(() => {
          timedOut = true;
          controller.abort(new RequestTimeoutError(url, timeoutMs));
        }, timeoutMs);

  // A caller's cancellation must still win — unmount and navigation depend
  // on it — so forward it rather than replacing their signal with ours.
  if (external) {
    if (external.aborted) {
      controller.abort(external.reason);
    } else {
      external.addEventListener('abort', () => controller.abort(external.reason), {
        once: true,
      });
    }
  }

  return {
    signal: controller.signal,
    settle: () => {
      if (timer !== null) clearTimeout(timer);
    },
    timedOut: () => timedOut,
  };
};

/**
 * Resolve a call's timeout: absent means default, explicit `null` means none.
 *
 * `??` would be wrong here — it treats a deliberate `null` (opt out) as
 * "unset" and silently reinstates the default.
 */
export const resolveTimeout = (options?: {
  timeoutMs?: number | null;
}): number | null =>
  options?.timeoutMs === undefined ? DEFAULT_REQUEST_TIMEOUT_MS : options.timeoutMs;
